import { z } from "zod"

/**
 * Password validation schema matching AWS Cognito configuration in Terraform
 * Requirements from terraform/modules/cognito/main.tf:
 * - Minimum length: 8 characters
 * - Requires lowercase letters
 * - Requires uppercase letters
 * - Requires numbers
 * - Does NOT require symbols
 */
export const cognitoPasswordSchema = z
  .string()
  .min(8, "Password must be at least 8 characters long")
  .regex(/[a-z]/, "Password must contain at least one lowercase letter")
  .regex(/[A-Z]/, "Password must contain at least one uppercase letter")
  .regex(/[0-9]/, "Password must contain at least one number")

/**
 * Email validation schema for Cognito
 */
export const cognitoEmailSchema = z
  .string()
  .min(1, "Email is required")
  .email("Please enter a valid email address")

/**
 * Username validation schema (Cognito uses email as username)
 */
export const cognitoUsernameSchema = cognitoEmailSchema

/**
 * Verification code schema (6-digit code from Cognito)
 */
export const cognitoVerificationCodeSchema = z
  .string()
  .length(6, "Verification code must be 6 digits")
  .regex(/^\d{6}$/, "Verification code must contain only numbers")

/**
 * Complete login form schema
 */
export const loginSchema = z.object({
  email: cognitoEmailSchema,
  password: z.string().min(1, "Password is required"),
})

/**
 * Complete signup form schema
 */
export const signupSchema = z.object({
  email: cognitoEmailSchema,
  password: cognitoPasswordSchema,
  firstName: z
    .string()
    .min(1, "First name is required")
    .max(100, "First name must be at most 100 characters"),
  lastName: z
    .string()
    .min(1, "Last name is required")
    .max(100, "Last name must be at most 100 characters"),
})

/**
 * Email verification form schema
 */
export const confirmEmailSchema = z.object({
  code: cognitoVerificationCodeSchema,
})

/**
 * Forgot password form schema
 */
export const forgotPasswordSchema = z.object({
  email: cognitoEmailSchema,
})

/**
 * Reset password form schema
 */
export const resetPasswordSchema = z
  .object({
    code: cognitoVerificationCodeSchema,
    newPassword: cognitoPasswordSchema,
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })

/**
 * Phone number validation (E.164 format)
 */
export const phoneNumberSchema = z
  .string()
  .regex(
    /^\+[1-9]\d{1,14}$/,
    "Phone number must be in E.164 format (e.g., +12125551234)"
  )

/**
 * SMS verification code form schema
 */
export const smsVerificationSchema = z.object({
  code: cognitoVerificationCodeSchema,
})

/**
 * Recovery code schema (8-character alphanumeric)
 */
export const recoveryCodeSchema = z
  .string()
  .length(8, "Recovery code must be 8 characters")
  .regex(
    /^[A-Z0-9]{8}$/i,
    "Recovery code must contain only letters and numbers"
  )

/**
 * MFA challenge form schema (6-digit code during login)
 */
export const mfaChallengeSchema = z.object({
  code: cognitoVerificationCodeSchema,
  trustDevice: z.boolean(),
})

/**
 * New password required form schema (admin-created users must set password on first login)
 */
export const newPasswordRequiredSchema = z
  .object({
    newPassword: cognitoPasswordSchema,
    confirmPassword: z.string().min(1, "Please confirm your password"),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: "Passwords do not match",
    path: ["confirmPassword"],
  })

/**
 * Recovery code login form schema (8-character alphanumeric code during login)
 */
export const recoveryCodeLoginSchema = z.object({
  code: recoveryCodeSchema,
})

// Type exports
export type LoginFormData = z.infer<typeof loginSchema>
export type SignupFormData = z.infer<typeof signupSchema>
export type ConfirmEmailFormData = z.infer<typeof confirmEmailSchema>
export type ForgotPasswordFormData = z.infer<typeof forgotPasswordSchema>
export type ResetPasswordFormData = z.infer<typeof resetPasswordSchema>
export type SmsVerificationFormData = z.infer<typeof smsVerificationSchema>
export type MFAChallengeFormData = z.infer<typeof mfaChallengeSchema>
export type NewPasswordRequiredFormData = z.infer<
  typeof newPasswordRequiredSchema
>
export type RecoveryCodeLoginFormData = z.infer<typeof recoveryCodeLoginSchema>
